import { Link, NavLink, Outlet } from "react-router-dom";
import { SearchBar } from "./SearchBar";
import { useMe, useTheme } from "../lib/useMe";
import { useCompare } from "../lib/compareStore";
import { MIN_COMPARE } from "../../shared/compare";
import { api } from "../lib/api";
import { personName } from "../../shared/people";

function Brand() {
  return (
    <Link to="/" className="brand">
      {/* A monogram C whose tail rises into a check mark: an open loop closed by
          proof. One continuous stroke, so it still reads at favicon size. */}
      <svg className="mark" viewBox="0 0 32 32" aria-hidden>
        <path
          d="M21.16 8.63 A9 9 0 1 0 21.16 23.37 L27.8 10.9"
          fill="none"
          stroke="var(--accent)"
          strokeWidth="4.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      CryptoChit
    </Link>
  );
}

export function Layout() {
  const { me, loading, refresh } = useMe();
  const { toggle } = useTheme();
  const cmp = useCompare();

  const goToSso = () =>
    (window.location.href = "/auth/login?returnTo=" + encodeURIComponent(location.pathname));

  const signIn = () => {
    // The server decides which methods exist; the UI only reflects it. Default
    // to the identity provider when the flag is missing, so a stale or failed
    // /me response can never surface the dev prompt on a real deployment.
    const methods = me?.auth ?? { sso: true, devSignIn: false };

    if (methods.sso) return goToSso();

    if (methods.devSignIn) {
      const handle = prompt("Dev sign-in: enter a username to impersonate");
      if (handle) {
        api.setDevUser(handle);
        refresh();
      }
      return;
    }

    alert("Sign-in is not configured for this deployment yet.");
  };

  const signOut = async () => {
    api.setDevUser(null);
    await api.me().catch(() => {});
    await fetch("/auth/logout", { method: "POST" }).catch(() => {});
    refresh();
  };

  return (
    <>
      <header className="site-header">
        <div className="container inner">
          <Brand />
          <nav className="nav">
            <NavLink to="/tokens">Tokens</NavLink>
            <NavLink to="/companies">Companies</NavLink>
            <NavLink to="/methodology">Methodology</NavLink>
          </nav>
          <div className="header-spacer" />
          <SearchBar />
          {/* Both glyphs are always in the markup and CSS decides which shows.
              Rendering one or the other from `theme` made the server (which
              cannot know the visitor's theme) emit different text from the
              client, and React discards the entire server tree on a text
              mismatch which is what produced the blank first paint. */}
          <button className="icon-btn theme-toggle" onClick={toggle} aria-label="Toggle theme" title="Toggle theme">
            <span className="when-light" aria-hidden>☾</span>
            <span className="when-dark" aria-hidden>☀</span>
          </button>
          {!loading &&
            (me?.user ? (
              <div className="row gap-8">
                {me.user.isStaff && (
                  <Link to="/admin" className="btn btn-ghost btn-sm">Admin</Link>
                )}
                <Link to="/dashboard" className="btn btn-ghost btn-sm">Dashboard</Link>
                {/* Both name fields come from identity-provider claims and can
                    be absent, which rendered an empty pill linking to /u/null. */}
                <Link
                  to={me.user.username ? `/u/${me.user.username}` : "/dashboard"}
                  className="btn btn-ghost btn-sm"
                >
                  {personName(me.user, "Account")}
                </Link>
                <button className="btn btn-ghost btn-sm" onClick={signOut}>Sign out</button>
              </div>
            ) : (
              <button className="btn btn-primary btn-sm" onClick={signIn}>Sign in</button>
            ))}
        </div>
      </header>

      <main className="container page">
        <Outlet />
      </main>

      {cmp.ids.length > 0 && (
        <div className="compare-tray" role="region" aria-label="Comparison selection">
          <div className="container row u-2d7d272">
            <span>{cmp.ids.length} selected for comparison</span>
            <div className="row gap-8">
              <button className="btn btn-ghost btn-sm" onClick={cmp.clear}>Clear</button>
              {cmp.ids.length >= MIN_COMPARE ? (
                <Link className="btn btn-primary btn-sm" to={`/compare?ids=${cmp.ids.join(",")}`}>
                  Compare {cmp.ids.length}
                </Link>
              ) : (
                <span className="muted u-1e8f47b">
                  Pick {MIN_COMPARE - cmp.ids.length} more
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <footer className="site-footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <Brand />
              <p className="footer-tagline">
                Know before you trust. Reviews backed by proof of usage.
              </p>
              <p className="footer-nfa">
                <strong>Not financial advice.</strong> CryptoChit publishes user opinions and
                verifiable on-chain facts - never investment advice, price predictions or
                buy/sell recommendations. Always do your own research.
              </p>
            </div>

            <nav className="footer-col" aria-label="Browse">
              <h2>Browse</h2>
              <Link to="/tokens">Tokens</Link>
              <Link to="/companies">Exchanges &amp; wallets</Link>
              <Link to="/compare">Compare side by side</Link>
              <Link to="/methodology">How verification works</Link>
            </nav>

            <nav className="footer-col" aria-label="Categories">
              <h2>Categories</h2>
              <Link to="/category/centralized-exchanges">Centralized exchanges</Link>
              <Link to="/category/hardware-wallets">Hardware wallets</Link>
              <Link to="/category/wallets">Software wallets</Link>
              <Link to="/category/custody">Custody &amp; institutional</Link>
              <Link to="/category/stablecoins">Stablecoins</Link>
              <Link to="/category/layer-1">Layer 1</Link>
              <Link to="/category/defi">DeFi</Link>
              <Link to="/category/tokenized-assets">Tokenized assets</Link>
            </nav>

            <nav className="footer-col" aria-label="For vendors">
              <h2>For vendors</h2>
              <Link to="/dashboard/claim">Claim your profile</Link>
              <Link to="/dashboard/submit">Submit a listing</Link>
              <Link to="/dashboard/reviews">Reply to reviews</Link>
              <Link to="/dashboard/developer">API &amp; badge</Link>
            </nav>

            <nav className="footer-col" aria-label="Trust and policies">
              <h2>Trust</h2>
              <Link to="/about">About CryptoChit</Link>
              <Link to="/methodology">Scoring methodology</Link>
              <Link to="/privacy">Privacy</Link>
              <Link to="/privacy#cookies">Cookies &amp; analytics</Link>
            </nav>
          </div>

          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} CryptoChit</p>
            {/* Deliberately not the home page's line word for word: repeating it
                here made e2e/home-messaging.spec.ts match two elements, and a
                slogan printed twice on one page reads as filler anyway. */}
            <p className="footer-bottom-line">Ratings are earned, never sold.</p>
          </div>
        </div>
      </footer>
    </>
  );
}
